export default function FeedHelp() {
  return (
    <div className="space-y-8 text-sm leading-7">

      <section>
        <h2 className="text-xl font-semibold mb-2">
          Feed
        </h2>

        <p className="text-neutral-600 dark:text-neutral-400">
          The Feed lets you share updates, photos, videos and interact
          with other members of the Metisel community.
        </p>
      </section>

      {/* ====================================================== */}

      <section>
        <h3 className="text-lg font-semibold mb-2">
          For You and Following
        </h3>

        <p>
          The Feed has two tabs at the top of the screen:
        </p>

        <ul className="list-disc ml-6 mt-2 space-y-2">
          <li>
            <strong>For You</strong> — posts from across the Metisel
            community.
          </li>

          <li>
            <strong>Following</strong> — posts only from people you
            follow.
          </li>
        </ul>

        <p className="mt-3 text-neutral-600 dark:text-neutral-400">
          While you're browsing <strong>For You</strong>, new posts from
          other members appear in a <strong>"N new posts"</strong> badge
          near the top of the screen instead of being inserted
          automatically. Tap the badge to jump to the top and load them.
        </p>
      </section>

      {/* ====================================================== */}

      <section>
        <h3 className="text-lg font-semibold mb-2">
          Creating a Post
        </h3>

        <ol className="list-decimal ml-6 space-y-2">
          <li>Open the Feed.</li>
          <li>Select the post composer.</li>
          <li>Enter your message.</li>
          <li>Optionally add up to four photos or videos.</li>
          <li>Select <strong>Post</strong>.</li>
        </ol>

        <p className="mt-3 text-neutral-600 dark:text-neutral-400">
          Your post becomes visible to other users immediately after it
          is published.
        </p>
      </section>

      {/* ====================================================== */}

      <section>
        <h3 className="text-lg font-semibold mb-2">
          Editing a Post
        </h3>

        <ol className="list-decimal ml-6 space-y-2">
          <li>Open your post.</li>
          <li>Select the <strong>⋮</strong> menu.</li>
          <li>Select <strong>Edit</strong>.</li>
          <li>Update the text.</li>
          <li>Select <strong>Update</strong> to save your changes.</li>
        </ol>

        <p className="mt-3 text-neutral-600 dark:text-neutral-400">
          Edited posts are marked as <strong>(edited)</strong>.
        </p>
      </section>

      {/* ====================================================== */}

      <section>
        <h3 className="text-lg font-semibold mb-2">
          Deleting a Post
        </h3>

        <ol className="list-decimal ml-6 space-y-2">
          <li>Open your post.</li>
          <li>Select the <strong>⋮</strong> menu.</li>
          <li>Select <strong>Delete</strong>.</li>
          <li>Confirm the deletion.</li>
        </ol>

        <p className="mt-3 text-neutral-600 dark:text-neutral-400">
          Deleted posts are no longer visible as active content.
        </p>
      </section>

      {/* ====================================================== */}

      <section>
        <h3 className="text-lg font-semibold mb-2">
          Liking Posts and Comments
        </h3>

        <p>
          Tap the <strong>like</strong> icon on any post or comment to
          like it. Tap it again to remove your like.
        </p>
      </section>

      {/* ====================================================== */}

      <section>
        <h3 className="text-lg font-semibold mb-2">
          Commenting and Replying
        </h3>

        <ol className="list-decimal ml-6 space-y-2">
          <li>Open a post.</li>
          <li>
            Select <strong>Reply</strong> at the bottom of the post to
            write a top-level comment.
          </li>
          <li>
            Optionally attach photos or videos to your comment.
          </li>
          <li>Submit your reply.</li>
        </ol>

        <p className="mt-3">
          You can also reply directly to another comment to keep the
          discussion threaded — tap <strong>Reply</strong> on that
          comment, and your response will appear nested underneath it.
        </p>

        <p className="mt-3 text-neutral-600 dark:text-neutral-400">
          You can edit or delete your own comments, and like or repost
          any comment, the same way you would a post.
        </p>
      </section>

      {/* ====================================================== */}

      <section>
        <h3 className="text-lg font-semibold mb-2">
          Reposting a Post or Comment
        </h3>

        <p>
          Reposting allows you to share another user's post or comment
          with your followers.
        </p>

        <ol className="list-decimal ml-6 mt-2 space-y-2">
          <li>Select the <strong>Repost</strong> button.</li>
          <li>Choose one of the available options:</li>
        </ol>

        <ul className="list-disc ml-12 mt-2 space-y-2">
          <li>
            <strong>Repost</strong> — Shares the original post or
            comment exactly as it is.
          </li>

          <li>
            <strong>Quote</strong> — Lets you add your own comment before
            sharing the original post or comment.
          </li>
        </ul>
      </section>

      {/* ====================================================== */}

      <section>
        <h3 className="text-lg font-semibold mb-2">
          Supporting a Creator
        </h3>

        <p>
          You can support creators by sending USWD directly from your
          wallet, on either a post or a comment.
        </p>

        <ol className="list-decimal ml-6 mt-2 space-y-2">
          <li>Select the <strong>Support</strong> button.</li>
          <li>
            Enter the amount of USWD you want to send, or choose one of
            the quick-select amounts.
          </li>
          <li>
            Review the amount, the network fee, and the total before
            confirming.
          </li>
          <li>Confirm the payment.</li>
        </ol>

        <p className="mt-3 text-neutral-600 dark:text-neutral-400">
          The creator receives the supported amount directly through the
          Metisel platform. You'll see a receipt once the transfer
          completes.
        </p>
      </section>

      {/* ====================================================== */}

      <section>
        <h3 className="text-lg font-semibold mb-2">
          Viewing Your Earnings
        </h3>

        <p>
          If you're the author of a post or comment, you can check how
          much USWD it has received in support.
        </p>

        <ol className="list-decimal ml-6 mt-2 space-y-2">
          <li>Open the <strong>⋮</strong> menu or the earnings option
            on your post or comment.
          </li>
          <li>
            View the total USWD received for that post or comment.
          </li>
        </ol>
      </section>

      {/* ====================================================== */}

      <section>
        <h3 className="text-lg font-semibold mb-2">
          My Posts
        </h3>

        <p>
          Open <strong>My Posts</strong> from your profile to see every
          post you've published, including reposts and quotes. From
          here you can open, edit, delete, or check the earnings of any
          of your posts.
        </p>
      </section>

      {/* ====================================================== */}

      <section>
        <h3 className="text-lg font-semibold mb-2">
          Notifications
        </h3>

        <p>
          The <strong>Notifications</strong> tab shows activity on your
          posts and comments, including:
        </p>

        <ul className="list-disc ml-6 mt-2 space-y-2">
          <li>Likes on your posts and comments</li>
          <li>Reposts and quotes of your posts and comments</li>
          <li>New comments on your posts</li>
          <li>USWD support sent to your posts and comments</li>
        </ul>

        <p className="mt-3 text-neutral-600 dark:text-neutral-400">
          Tap a notification to jump straight to the related post or
          comment.
        </p>
      </section>

      {/* ====================================================== */}

      <section>
        <h3 className="text-lg font-semibold mb-2">
          Reporting a Post
        </h3>

        <p>
          If you believe a post violates community standards, you can
          report it.
        </p>

        <ol className="list-decimal ml-6 mt-2 space-y-2">
          <li>Open the <strong>⋮</strong> menu on the post.</li>
          <li>Select <strong>Report</strong>.</li>
          <li>Select the appropriate reason.</li>
          <li>Submit your report.</li>
        </ol>

        <p className="mt-3 text-neutral-600 dark:text-neutral-400">
          Reports are reviewed by the Metisel moderation system.
        </p>
      </section>

    </div>
  );
}